import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { message } from 'antd';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import { Routes } from 'config/routes';
import {
  DonationOutput,
  useGetDonationQuery,
  useUpdateDonationDonorMutation,
  useUpdateDonationMutation,
  WeatherType,
} from 'gql/types';
import { Spin } from 'modules/common/components';
import { UnsavedChangesModal } from 'modules/common/components/UnsavedChangesModal';
import { useUnsavedChangesWarning } from 'modules/common/hooks/useUnsavedChangesWarning';
import { GET_DONATION_QUERY } from 'modules/donations/graphql/queries';
import { isFailedDonation } from 'modules/donations/utils/isFailedDonation';
import { useDonorAvatarUrl } from 'modules/donor/hooks/useDonorAvatarUrl';
import { FC, useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';

import DesktopEditDonationFormView from './DesktopEditDonationFormView';
import { mapDefaultValues } from './mapDefaultValues';
import MobileEditDonationFormView from './MobileEditDonationFormView';
import { editDonationSchema, EditDonationSchemaType } from './schema';

const EditDonationForm: FC = () => {
  const { t } = useTranslation('donation.EditDonationForm');

  const breakpoint = useBreakpoint();

  const navigate = useNavigate();
  const params = useParams();
  const donationShortId = params.id || '';

  const [formEnabled, setFormEnabled] = useState<boolean>(false);
  const [isDonorChangePending, setIsDonorChangePending] =
    useState<boolean>(false);

  const editDonationForm = useForm<EditDonationSchemaType>({
    resolver: yupResolver(editDonationSchema),
    mode: 'onChange',
  });

  const {
    formState: { isDirty, isValid: isFormValid },
    handleSubmit,
    reset,
    watch,
    trigger,
    getFieldState,
  } = editDonationForm;

  const ineligible = watch('examination.ineligible');
  const ineligibleReason = watch('examination.ineligibleReason');

  const { data: donationData, loading: loadingDonationData } =
    useGetDonationQuery({
      variables: {
        input: {
          id: donationShortId,
        },
      },
      onCompleted: (data) => {
        const defaultFormValues = mapDefaultValues(
          data.getDonation as DonationOutput
        );
        reset(defaultFormValues);
      },
      onError() {
        navigate(Routes.NotFound);
      },
    });
  const donation = donationData?.getDonation as DonationOutput | undefined;
  const originalDonorId = donation?.donor?.id || '';
  const productsCount = donation?.products?.length || 0;

  useEffect(() => {
    if (donationData?.getDonation) {
      const newDefaultValues = mapDefaultValues(
        donationData.getDonation as DonationOutput
      );
      reset(newDefaultValues);
    }
  }, [donationData, reset]);

  const donationUUID = donationData?.getDonation.id;

  const [editDonation, { loading: loadingEditingDonation }] =
    useUpdateDonationMutation({
      update(cache) {
        cache.evict({ fieldName: 'getProducts' });
      },
      refetchQueries: [
        {
          query: GET_DONATION_QUERY,
          variables: {
            input: {
              id: donationShortId,
            },
          },
        },
      ],
      onCompleted: () => {
        reset();
        message.success(t('donationUpdated'));
      },
      onError: (e) => {
        message.error(e.message);
      },
    });

  const [editDonationDonor, { loading: loadingEditingDonationDonor }] =
    useUpdateDonationDonorMutation({
      update(cache) {
        cache.evict({ fieldName: 'getProducts' });
        cache.evict({ fieldName: 'getSpecificDonorDonationsList' });
      },
      refetchQueries: [
        {
          query: GET_DONATION_QUERY,
          variables: {
            input: {
              id: donationShortId,
            },
          },
        },
      ],
      onCompleted: () => {
        reset();
        message.success(t('donationDonorUpdated'));
      },
      onError: (e) => {
        message.error(e.message);
      },
    });

  const donorPictures = donationData?.getDonation.donor?.pictures;
  const avatarUrl = useDonorAvatarUrl(donorPictures) || '';

  const essentialFormFields: (keyof EditDonationSchemaType)[] = [
    'donationInfo',
    'environment',
    'examination',
    'quantity',
    'donationDate',
  ];
  const isEssentialDataUpdated = essentialFormFields.some(
    (key) => getFieldState(key).isDirty
  );
  const donorIdIsChanged = getFieldState('donorId').isDirty;

  const submitForm = async (data: EditDonationSchemaType) => {
    const {
      examination,
      donationInfo,
      environment,
      quantity,
      donorId,
      donationDate,
    } = data;
    const { species, ...restExaminationFields } = examination;

    if (isEssentialDataUpdated) {
      const isFailed = isFailedDonation({
        units: quantity.unit,
        approvedMl: quantity.approvedDonationsMl,
      });

      await editDonation({
        variables: {
          input: {
            donationDate,
            id: donationUUID as string,
            ...restExaminationFields,
            ...donationInfo,
            ...environment,
            ...quantity,
            failedReason: isFailed ? quantity.failedReason ?? null : null,
            ineligibleReason: ineligible ? ineligibleReason : null,
            weatherTypes: environment.weatherTypes as WeatherType[],
          },
        },
      });
    }

    if (donorIdIsChanged) {
      await editDonationDonor({
        variables: {
          input: {
            id: donationUUID as string,
            donorId,
          },
        },
      });
    }

    setFormEnabled(false);
  };

  const saveHandler = handleSubmit(submitForm);

  const handleSave = async () => {
    const formData = editDonationForm.getValues();
    await submitForm(formData);
  };

  const closeHandler = () => {
    const defaultFormValues = donation ? mapDefaultValues(donation) : undefined;
    reset(defaultFormValues);
    setFormEnabled(false);
    setIsDonorChangePending(false);
  };

  const editHandler = async () => {
    setFormEnabled(!formEnabled);
    const defaultFormValues = donation ? mapDefaultValues(donation) : undefined;
    reset(defaultFormValues);
    await trigger();
  };

  const {
    isModalOpen,
    handleStay,
    handleDiscard,
    handleSaveAndLeave,
    canSaveAndLeave,
  } = useUnsavedChangesWarning({
    isDirty,
    onSave: handleSave,
    isFormValid,
  });

  const loading =
    loadingEditingDonation ||
    loadingDonationData ||
    loadingEditingDonationDonor;

  return (
    <>
      <Spin spinning={loading}>
        <FormProvider {...editDonationForm}>
          {breakpoint.xl && (
            <DesktopEditDonationFormView
              isFormValid={isFormValid}
              formEnabled={formEnabled}
              closeHandler={closeHandler}
              saveHandler={saveHandler}
              editHandler={editHandler}
              pictureUrl={avatarUrl}
              donationId={donationShortId}
              originalDonorId={originalDonorId}
              productsCount={productsCount}
              isDonorChangePending={isDonorChangePending}
              onDonorChangePending={setIsDonorChangePending}
            />
          )}
          {!breakpoint.xl && (
            <MobileEditDonationFormView
              isFormValid={isFormValid}
              formEnabled={formEnabled}
              closeHandler={closeHandler}
              saveHandler={saveHandler}
              editHandler={editHandler}
              pictureUrl={avatarUrl}
              donationId={donationShortId}
              originalDonorId={originalDonorId}
              productsCount={productsCount}
              isDonorChangePending={isDonorChangePending}
              onDonorChangePending={setIsDonorChangePending}
            />
          )}
        </FormProvider>
      </Spin>

      <UnsavedChangesModal
        isOpen={isModalOpen}
        onStay={handleStay}
        onDiscard={handleDiscard}
        onSaveAndLeave={handleSaveAndLeave}
        canSaveAndLeave={canSaveAndLeave}
        isSaving={loading}
      />
    </>
  );
};

export default EditDonationForm;
